import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupsAPI } from '../services/api';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { QuickAddMember, NewMember } from '../components/features/QuickAddMember';

interface LocalMember extends NewMember {
  id: string;
}

const CreateGroup: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState<LocalMember[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAddMember = (member: NewMember) => {
    setMembers([
      ...members,
      {
        ...member,
        id: `temp-${Date.now()}`,
      },
    ]);
  };

  const handleRemoveMember = (id: string) => {
    setMembers(members.filter((m) => m.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter a group name');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Create the group
      const result = await groupsAPI.create({
        name: name.trim(),
        description: description.trim() || undefined,
      });

      // Add members to the group
      for (const member of members) {
        await groupsAPI.addMember(result.group.id, {
          guestName: member.name,
          isVegetarian: member.isVegetarian,
          drinksAlcohol: member.drinksAlcohol,
        });
      }

      navigate(`/groups/${result.group.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Create Group" showBack />

      <main className="p-4 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Group Details Card */}
          <Card>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-3xl">
                  {name ? (
                    name.toLowerCase().includes('trip') ? '✈️' :
                    name.toLowerCase().includes('dinner') ? '🍽️' :
                    name.toLowerCase().includes('party') ? '🎉' :
                    name.toLowerCase().includes('home') ? '🏠' :
                    '👥'
                  ) : '👥'}
                </div>
                <div className="flex-1">
                  <Input
                    placeholder="Group name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="text-lg font-semibold"
                  />
                </div>
              </div>
              <Input
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </Card>

          {/* Members Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">
                Members {members.length > 0 && `(${members.length})`}
              </h2>
              <button
                type="button"
                onClick={() => setShowAddMember(true)}
                className="text-sm text-green-600 font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Person
              </button>
            </div>

            {members.length === 0 ? (
              <Card>
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">Add your first member</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    You can add members now or later
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowAddMember(true)}
                  >
                    Add Person
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <Card key={member.id} padding="sm">
                    <div className="flex items-center gap-3">
                      <Avatar name={member.name} size="md" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{member.name}</p>
                        <div className="flex gap-1.5 mt-0.5">
                          {member.isVegetarian ? (
                            <Badge variant="veg" size="sm">Veg</Badge>
                          ) : (
                            <Badge variant="nonveg" size="sm">Non-veg</Badge>
                          )}
                          {member.drinksAlcohol && (
                            <Badge variant="alcohol" size="sm">Drinks</Badge>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member.id)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-red-500"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Tip */}
          <Card className="bg-blue-50 border-blue-100">
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-blue-900 font-medium">Pro tip</p>
                <p className="text-sm text-blue-700">
                  Set dietary preferences for each member to split bills fairly based on what they eat and drink.
                </p>
              </div>
            </div>
          </Card>

          {/* Submit Button */}
          <Button type="submit" loading={loading} fullWidth size="lg">
            Create Group
          </Button>
        </form>
      </main>

      <QuickAddMember
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
      />
    </div>
  );
};

export default CreateGroup;
